"use client";

import { useState, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ShadowCard from "@/components/common/ShadowCard";
import Button from "@/components/ui/Button";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { CiCirclePlus, CiCircleMinus } from "react-icons/ci";
import ScheduleCallModal from "@/components/common/ScheduleCallModal";

const BUSINESS_OPTIONS = [
  { value: "", label: "Select your business" },
  { value: "restaurant", label: "Restaurant" },
  { value: "gas-station", label: "Gas Station" },
  { value: "convenience-store", label: "Convenience Store" },
  { value: "bar", label: "Bar" },
  { value: "grocery-store", label: "Grocery Store" },
];

const MONTHLY_RATE_PER_MACHINE = 1250;

const formatCurrency = (amount: number): string => {
  return `$${amount.toLocaleString()}`;
};

export default function IncomeCalculatorContent() {
  const [formData, setFormData] = useState({
    business: "",
    machines: 0,
  });
  const [calculatedIncome, setCalculatedIncome] = useState(0);
  const [showError, setShowError] = useState(false);
  const [showMachineError, setShowMachineError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const handleCalculate = useCallback(() => {
    let hasError = false;

    if (!formData.business) {
      setShowError(true);
      hasError = true;
    } else {
      setShowError(false);
    }

    // Validate machines count
    if (formData.machines === 0) {
      setShowMachineError(true);
      hasError = true;
    } else {
      setShowMachineError(false);
    }

    if (hasError) {
      setCalculatedIncome(0);
      setShowResults(false);
      return;
    }

    const monthlyIncome = formData.machines * MONTHLY_RATE_PER_MACHINE;
    setCalculatedIncome(monthlyIncome);
    setShowResults(true);

    // Scroll to the top of the section when results are calculated
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [formData.business, formData.machines]);

  const handleIncrement = useCallback(() => {
    setFormData((prev) => ({ ...prev, machines: prev.machines + 1 }));
    setShowMachineError(false);
  }, []);

  const handleDecrement = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      machines: prev.machines > 1 ? prev.machines - 1 : prev.machines,
    }));
    setShowMachineError(false);
  }, []);

  const handleBusinessChange = useCallback((value: string) => {
    setFormData((prev) => ({ ...prev, business: value }));
    setShowError(false);
  }, []);

  const handleModalOpen = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const formattedIncome = useMemo(
    () => formatCurrency(calculatedIncome),
    [calculatedIncome]
  );

  return (
    <main className="bg-brand-black min-h-screen">
      {/* Main Section */}
      <section
        ref={sectionRef}
        className="relative flex min-h-screen items-center px-5 py-24 md:px-12 lg:px-[100px]"
      >
        <div className="relative z-10 flex flex-col gap-[100px] lg:flex-row lg:items-center lg:justify-between lg:gap-16">
          {/* Left Content */}
          <div className="flex-1 text-center lg:max-w-2xl lg:text-left">
            {/* Headline */}
            <h1 className="font-raleway mb-[10px] text-[2rem] leading-[1.3] font-bold tracking-wide text-white md:text-4xl lg:mb-[20px]">
              <span className="block lg:inline">Calculate the </span>{" "}
              <span className="from-brand-red to-brand-dark-red block bg-linear-to-br bg-clip-text text-transparent lg:inline">
                Passive Income Stream
              </span>{" "}
              <span className="block lg:inline">
                You Could Be Earning From Your Unused Floor Space Right Now
              </span>
            </h1>

            {/* Description */}
            <p className="font-raleway mb-10 text-center text-base leading-[1.3] text-white/80 md:text-start">
              Stop guessing and start planning. Our calculator uses real-world
              market data to estimate the monthly cash flow your business is
              currently missing out on. Simply adjust the sliders to match your
              foot traffic and discover how much the Georgia Lottery
              Corporation&apos;s regulated gaming program can contribute to your
              bottom line every single month with zero investment.
            </p>

            {/* CTA Button */}
            <div className="flex justify-center md:justify-start">
              <Button variant="gradient" onClick={handleModalOpen}>
                Schedule a Call
              </Button>
            </div>
          </div>

          <ScheduleCallModal isOpen={isModalOpen} onClose={handleModalClose} />
          {/* Right Content - Calculator Card */}
          <div className="w-full lg:w-auto lg:max-w-[530px] lg:min-w-[530px]">
            <ShadowCard className="from-brand-red to-brand-dark-red bg-linear-to-br p-4.5">
              <h3 className="font-raleway mb-2.5 text-left text-base font-bold text-white">
                How will your money grow?
              </h3>

              <div className="flex flex-col">
                {/* Business Type Dropdown */}
                <CustomDropdown
                  value={formData.business}
                  options={BUSINESS_OPTIONS}
                  onChange={handleBusinessChange}
                  placeholder="Select your business"
                />

                {/* Error Message */}
                {showError && (
                  <p className="font-raleway mt-2 text-xs text-white">
                    Please select your business type
                  </p>
                )}

                {/* Number of Machines */}
                <div className="mt-5">
                  <div className="flex items-center justify-between">
                    <label className="font-raleway text-base font-bold text-white">
                      Number of Machines
                    </label>
                    <div className="flex items-center gap-4 rounded-full border border-white bg-transparent px-[10px] py-[5px]">
                      <button
                        onClick={handleDecrement}
                        className="flex cursor-pointer items-center justify-center text-white hover:opacity-80"
                      >
                        <CiCircleMinus className="h-6 w-6" />
                      </button>
                      <span className="font-raleway min-w-[30px] text-center text-base font-bold text-white">
                        {formData.machines}
                      </span>
                      <button
                        onClick={handleIncrement}
                        className="flex cursor-pointer items-center justify-center text-white hover:opacity-80"
                      >
                        <CiCirclePlus className="h-6 w-6" />
                      </button>
                    </div>
                  </div>

                  {/* Machine Error Message */}
                  {showMachineError && (
                    <p className="font-raleway mt-2 text-xs text-white">
                      Please select at least 1 machine
                    </p>
                  )}
                </div>

                {/* Calculate Button */}
                <div className="mt-10 flex justify-center">
                  <Button variant="white" onClick={handleCalculate}>
                    Calculate
                  </Button>
                </div>

                {/* Results Section */}
                <div className="mt-10 flex flex-col gap-5">
                  <AnimatePresence mode="wait">
                    {showResults && (
                      <>
                        <motion.div
                          key="monthly-income"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.5, ease: "easeOut" }}
                          className="rounded-2xl border border-[#EE4137] bg-white/20 px-5 py-1 text-center backdrop-blur-sm"
                        >
                          <p className="font-goldman text-[72px] leading-none font-bold tracking-tight text-white">
                            {formattedIncome}
                          </p>
                          <p className="font-raleway mt-1 text-base text-white/90">
                            Estimated Monthly Income
                          </p>
                        </motion.div>
                        <motion.div
                          key="machine-earnings"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{
                            duration: 0.5,
                            delay: 0.2,
                            ease: "easeOut",
                          }}
                          className="rounded-2xl border border-[#EE4137] bg-white/20 px-5 py-1 text-center backdrop-blur-sm"
                        >
                          <p className="font-goldman text-[72px] leading-none font-bold tracking-tight text-white">
                            ${MONTHLY_RATE_PER_MACHINE.toLocaleString()}
                          </p>
                          <p className="font-raleway mt-1 text-base text-white/90">
                            Machine Earnings
                          </p>
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </ShadowCard>
          </div>
        </div>
      </section>
    </main>
  );
}
